import { Check, ChevronDown, Search } from "lucide-react";
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { MenuItem, MenuLabel, MenuSeparator, PopoverPanel, usePopover, type Align } from "./Overlay";

const fieldBase =
  "w-full rounded-lg border border-line-strong bg-surface-2 px-3 text-[13.5px] text-fg placeholder:text-subtle outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/25 disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { icon?: ReactNode; suffix?: ReactNode }>(function Input(
  { className, icon, suffix, ...rest },
  ref,
) {
  if (!icon && !suffix) return <input ref={ref} className={cn(fieldBase, "h-9", className)} {...rest} />;
  return (
    <div className={cn("relative flex items-center", className)}>
      {icon && <span className="pointer-events-none absolute left-3 text-subtle">{icon}</span>}
      <input ref={ref} className={cn(fieldBase, "h-9", icon && "pl-9", suffix && "pr-12")} {...rest} />
      {suffix && <span className="absolute right-3 text-xs text-muted">{suffix}</span>}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, rows = 3, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={cn(fieldBase, "resize-y py-2 leading-relaxed scrollbar-thin", className)} {...rest} />;
});

export function SearchInput({ value, onChange, placeholder = "Search…", className, autoFocus }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string; autoFocus?: boolean }) {
  return (
    <Input
      icon={<Search className="size-4" />}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={className}
      autoFocus={autoFocus}
      type="search"
    />
  );
}

export function Field({ label, hint, error, children, className, htmlFor, required }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; className?: string; htmlFor?: string; required?: boolean }) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-[12.5px] font-medium text-fg/90">
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      {children}
      {error ? <div className="text-xs text-danger">{error}</div> : hint ? <div className="text-xs text-muted">{hint}</div> : null}
    </div>
  );
}

export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function NativeSelect({ className, children, ...rest }, ref) {
  return (
    <div className={cn("relative", className)}>
      <select ref={ref} className={cn(fieldBase, "h-9 appearance-none pr-8")} {...rest}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
    </div>
  );
});

/** iOS-style switch. */
export function Toggle({ checked, onChange, disabled, label, size = "md" }: { checked: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label?: string; size?: "sm" | "md" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        "relative inline-flex shrink-0 items-center rounded-full transition-colors disabled:opacity-40",
        size === "sm" ? "h-4.5 w-8" : "h-5.5 w-10",
        checked ? "bg-primary" : "bg-[#3a3a3e]",
      )}
    >
      <span
        className={cn(
          "inline-block rounded-full bg-white shadow transition-transform",
          size === "sm" ? "size-3.5" : "size-4.5",
          checked ? (size === "sm" ? "translate-x-4" : "translate-x-5") : "translate-x-0.5",
        )}
      />
    </button>
  );
}

export function Checkbox({ checked, onChange, label, disabled, className, round }: { checked: boolean; onChange?: (v: boolean) => void; label?: ReactNode; disabled?: boolean; className?: string; round?: boolean }) {
  const id = useId();
  return (
    <label htmlFor={id} className={cn("inline-flex cursor-pointer items-center gap-2 text-[13px]", disabled && "cursor-not-allowed opacity-50", className)}>
      <span className="relative inline-flex">
        <input id={id} type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} />
        <span
          className={cn(
            "grid size-4.5 place-items-center border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-link",
            round ? "rounded-full" : "rounded-[5px]",
            checked ? "border-primary bg-primary text-white" : "border-line-strong bg-transparent",
          )}
        >
          {checked && <Check className="size-3" strokeWidth={3} />}
        </span>
      </span>
      {label}
    </label>
  );
}

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  group?: string;
  divider?: boolean;
}

/**
 * PlayAd dark pill dropdown ("📅 Last 7 days ▾"). Renders a radio list in a popover.
 * For multi-select pass `multiple` + `values`.
 */
export function SelectPill<V extends string = string>({
  value,
  values,
  multiple,
  options,
  onChange,
  onChangeMany,
  icon,
  placeholder,
  label,
  align = "start",
  className,
  panelClassName,
  size = "md",
  footer,
  variant = "pill",
}: {
  value?: V | null;
  values?: V[];
  multiple?: boolean;
  options: SelectOption<V>[];
  onChange?: (v: V) => void;
  onChangeMany?: (v: V[]) => void;
  icon?: ReactNode;
  placeholder?: string;
  /** Fixed trigger label (overrides selected label). */
  label?: ReactNode;
  align?: Align;
  className?: string;
  panelClassName?: string;
  size?: "sm" | "md";
  footer?: (close: () => void) => ReactNode;
  variant?: "pill" | "field";
}) {
  const p = usePopover();
  const sel = options.find((o) => o.value === value);
  const many = values ?? [];
  const triggerLabel =
    label ??
    (multiple
      ? many.length === 0
        ? placeholder
        : many.length === 1
          ? options.find((o) => o.value === many[0])?.label
          : `${placeholder ?? "Selected"} · ${many.length}`
      : (sel?.label ?? placeholder));
  let lastGroup: string | undefined;
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-haspopup="listbox"
        aria-expanded={p.open}
        className={cn(
          "inline-flex items-center gap-1.5 whitespace-nowrap text-fg transition-colors",
          variant === "pill"
            ? cn("rounded-lg border border-line bg-surface hover:bg-hover hover:border-line-strong", size === "sm" ? "h-7 px-2.5 text-xs" : "h-8.5 px-3 text-[13px]")
            : "h-9 w-full justify-between rounded-lg border border-line-strong bg-surface-2 px-3 text-[13.5px] hover:border-[#4a4a50]",
          p.open && "border-primary",
          className,
        )}
      >
        {icon && <span className="text-muted">{icon}</span>}
        <span className={cn("truncate", !sel && !multiple && !label && "text-muted", multiple && many.length === 0 && "text-fg/90")}>{triggerLabel}</span>
        <ChevronDown className={cn("size-3.5 shrink-0 text-muted transition-transform", p.open && "rotate-180")} />
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} align={align} className={cn("max-h-[360px] min-w-[200px] overflow-y-auto scrollbar-thin", panelClassName)} role="listbox" matchWidth={variant === "field"}>
        {options.map((o, i) => {
          const isSel = multiple ? many.includes(o.value) : o.value === value;
          const header = o.group && o.group !== lastGroup ? o.group : null;
          lastGroup = o.group;
          return (
            <div key={`${o.value}-${i}`}>
              {o.divider && <MenuSeparator />}
              {header && <MenuLabel>{header}</MenuLabel>}
              <MenuItem
                icon={o.icon}
                disabled={o.disabled}
                description={o.description}
                selected={false}
                onClick={() => {
                  if (multiple) {
                    const next = isSel ? many.filter((v) => v !== o.value) : [...many, o.value];
                    onChangeMany?.(next);
                  } else {
                    onChange?.(o.value);
                    p.close();
                  }
                }}
                right={
                  multiple ? (
                    <span className={cn("grid size-4 place-items-center rounded border", isSel ? "border-primary bg-primary text-white" : "border-line-strong")}>{isSel && <Check className="size-3" strokeWidth={3} />}</span>
                  ) : (
                    <span className={cn("grid size-4 place-items-center rounded-full border", isSel ? "border-white bg-white text-black" : "border-line-strong")}>{isSel && <Check className="size-2.5" strokeWidth={3.5} />}</span>
                  )
                }
              >
                {o.label}
              </MenuItem>
            </div>
          );
        })}
        {footer && (
          <>
            <MenuSeparator />
            {footer(p.close)}
          </>
        )}
      </PopoverPanel>
    </>
  );
}

/** Segmented control. `look="dark"` = CTR|CVR tabs; `look="white"` = Explore/Following/Inspired. */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  look = "dark",
  size = "md",
  className,
}: {
  value: V;
  onChange: (v: V) => void;
  options: { value: V; label: ReactNode; icon?: ReactNode; disabled?: boolean; count?: number }[];
  look?: "dark" | "white";
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div role="tablist" className={cn("inline-flex items-center gap-0.5 rounded-xl p-1", look === "dark" ? "bg-surface-2 border border-line" : "bg-elevated/70", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-40",
              size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-[13px]",
              look === "dark" && (active ? "bg-elevated-2 text-fg" : "text-muted hover:text-fg"),
              look === "white" && (active ? "bg-white text-black shadow-sm" : "text-muted hover:text-fg"),
            )}
          >
            {o.icon}
            {o.label}
            {o.count !== undefined && o.count > 0 && <span className={cn("rounded-full px-1.5 text-[10.5px] tnum", active && look === "white" ? "bg-black/10" : "bg-elevated")}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Underline tabs (settings sections, media library). */
export function Tabs<V extends string>({ value, onChange, options, className }: { value: V; onChange: (v: V) => void; options: { value: V; label: ReactNode; count?: number }[]; className?: string }) {
  return (
    <div role="tablist" className={cn("flex items-center gap-5 overflow-x-auto border-b border-line scrollbar-none", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn("-mb-px flex shrink-0 items-center gap-1.5 border-b-2 pb-2.5 pt-1 text-[13.5px] font-medium transition-colors", active ? "border-primary text-fg" : "border-transparent text-muted hover:text-fg")}
          >
            {o.label}
            {o.count !== undefined && <span className="rounded-full bg-elevated px-1.5 text-[10.5px] text-muted tnum">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Toggleable chip (filters, rejection reasons). */
export function Chip({ selected, onClick, children, icon, className, tone = "primary", disabled }: { selected?: boolean; onClick?: () => void; children: ReactNode; icon?: ReactNode; className?: string; tone?: "primary" | "danger"; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors disabled:opacity-40",
        selected
          ? tone === "danger"
            ? "border-danger/60 bg-danger-soft text-danger"
            : "border-primary/70 bg-primary-soft text-lavender"
          : "border-line-strong text-fg/85 hover:border-[#4a4a50] hover:bg-hover",
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}
